import React, { useEffect, useState, useMemo, useCallback } from 'react';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import { useTranslation } from 'react-i18next';
import AddAccountCardModal from '../Widgets/AddAccountCardModal/AddAccountCardModal.jsx';
import {
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    Popper,
    Tooltip,
    IconButton,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import nestedStyles from './AddBankModal.module.scss';
import fieldStyles from '../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';

const sameWidthModifier = {
    name: 'sameWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
        const width = `${state.rects.reference.width}px`;
        state.styles.popper.width = width;
        state.styles.popper.minWidth = width;
    },
};

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => {
    const { className, style, modifiers, ...other } = props;
    const width = props.anchorEl?.clientWidth ?? style?.width;
    return (
        <Popper
            {...other}
            ref={ref}
            dir={direction}
            className={`${className || ''} ${fieldStyles.comboPopper} ${nestedStyles.comboPopperHigh}`}
            placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
            modifiers={[
                ...(modifiers || []),
                { name: 'offset', options: { offset: [0, 8] } },
                { name: 'preventOverflow', options: { padding: 0 } },
                sameWidthModifier,
            ]}
            style={{
                ...style,
                width,
                minWidth: width,
            }}
        />
    );
});
ComboPopper.displayName = 'ComboPopper';

const AddBankModal = ({
    open,
    onClose,
    selectedBankForCreate,
    bankOptions,
    handleChangeBankForCreate,
    bankBranch,
    handleChangeCreateBank,
    bankAccountNumber,
    cardForNewBank,
    processedCardsData,
    setCardForNewBank,
    handleAddBank,
    client,
    token,
    setLoading,
    palCodes,
    fetchEnums,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const fieldTextAlign = direction === 'ltr' ? 'left' : 'right';
    const comboInputProps = { disableUnderline: true };

    const [creditDebitSearchTerm, setCreditDebitSearchTerm] = useState('');
    const [newAccountCard, setNewAccountCard] = useState({ accountCodeName: '', accountCodeId: '' });
    const [isEditAccountCard, setIsEditAccountCard] = useState(false);
    const [isAddAccountCard, setIsAddAccountCard] = useState(false);
    const [palCode, setPalCode] = useState('בנקים');
    const [newCardId, setNewCardId] = useState(null);
    const [addAccountCardType, setAddAccountCardType] = useState('cardForNewBank');
    const [cardIdOption, setCardIdOption] = useState(-1);

    const filteredBankOptions = bankOptions.filter(
        option => option.value !== '100' && option.value !== '102'
    );

    const isFormValid = useMemo(() => {
        return Boolean(
            selectedBankForCreate &&
            bankBranch.trim() &&
            bankAccountNumber.trim() &&
            cardForNewBank
        );
    }, [selectedBankForCreate, bankBranch, bankAccountNumber, cardForNewBank]);

    const formattedAccountCardOptions = processedCardsData.map((option) => ({
        value: option.cardId,
        label: option.accountCaseId
            ? `${option.name} - ${option?.accountCaseId || ''}`
            : `${option.name}`,
        name: option.name,
        accountCaseId: option.accountCaseId || '',
        cardId: option.cardId,
        value1: option.value1,
        palCode: option.palCode,
        isEditable: option.isEditable
    }));

    useEffect(() => {
        if (processedCardsData && processedCardsData.length > 0) {
            const maxCardId = Math.max(
                ...processedCardsData.slice(1).map((option) => parseInt(option.cardId, 10))
            );

            setCardIdOption(maxCardId + 1);
        }
    }, [processedCardsData]);

    const handleChangeAfterAdd = (cardId, accountType) => {
        const newCard = {
            cardId,
            type: accountType,
        };
        setNewCardId(newCard);
    };

    useEffect(() => {
        if (processedCardsData && processedCardsData.length > 0 && newCardId) {
            const selectedOption = processedCardsData.find(
                (option) => option.cardId === newCardId.cardId
            );
            if (newCardId.type === 'cardForNewBank') {
                setCardForNewBank(newCardId.cardId);
            }
            setNewCardId(null);
        }
    }, [processedCardsData, newCardId]);

    const EditAccountCard = (selectedItem, accountType) => {
        if (!selectedItem) {
            console.warn('No account card selected for editing.');
            return;
        }
        setPalCode(selectedItem.palCode);
        setNewAccountCard({
            accountCodeName: selectedItem.name || '',
            accountCodeId: selectedItem.cardId,
            accountCaseId: selectedItem.accountCaseId || '',
        });
        setIsEditAccountCard(true);
        setAddAccountCardType(accountType);
        setIsAddAccountCard(true);
    };

    const openAddAccountCard = (searchTerm = '') => {
        setIsEditAccountCard(false);
        setAddAccountCardType('cardForNewBank');
        setNewAccountCard({
            accountCodeName: searchTerm || '',
            accountCodeId: cardIdOption,
        });
        setIsAddAccountCard(true);
    };

    const handleCreditDebitForJournalEntry = (newValue) => {
        if (!newValue) {
            setCardForNewBank(null);
            return;
        }

        if (newValue.value1 === 'AddItem') {
            openAddAccountCard(newValue.searchTerm || '');
            return;
        }

        setCardForNewBank(newValue.cardId);
    };

    const handleNoOptionCreditDebitClick = (searchTerm) => {
        openAddAccountCard(searchTerm);
    };

    const renderComboPopper = useCallback((props) => (
        <ComboPopper {...props} direction={direction} />
    ), [direction]);

    return (
        <>
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            sx={{ zIndex: 1400 }}
            PaperProps={{
                className: nestedStyles.nestedPaper,
                elevation: 0,
            }}
        >
            <div className={direction === 'rtl' ? nestedStyles.rtl : nestedStyles.ltr} dir={direction}>
                <div className={nestedStyles.header}>
                    <IconButton onClick={onClose} className={nestedStyles.closeIcon}>
                        <CancelIcon className={nestedStyles.closeIcon} />
                    </IconButton>
                    <div>{t('SetBank')}</div>
                    <div />
                </div>

                <DialogContent className={`${nestedStyles.body} ${direction === 'rtl' ? fieldStyles.rtl : fieldStyles.ltr}`}>
                    <div>
                        <span className={nestedStyles.fieldLabel}>{t('SelectTemplate')} *</span>
                        <AutocompleteSelectFilter
                            options={filteredBankOptions}
                            selectedValues={selectedBankForCreate}
                            onChange={(event, value) => handleChangeBankForCreate(value)}
                            placeholder={t('SelectTemplate')}
                            isMultiple={false}
                            direction={direction}
                            searchable
                            portalMenu
                        />
                    </div>

                    <div>
                        <span className={nestedStyles.fieldLabel}>{t('BankBranch')} *</span>
                        <TextField
                            fullWidth
                            placeholder={t('BankBranch')}
                            value={bankBranch}
                            onChange={(e) => handleChangeCreateBank('bankBranch', e.target.value)}
                            variant="standard"
                            className={fieldStyles.styleInput}
                            InputProps={comboInputProps}
                            inputProps={{ style: { textAlign: fieldTextAlign } }}
                            required
                        />
                    </div>

                    <div>
                        <span className={nestedStyles.fieldLabel}>{t('BankAccountNumber')} *</span>
                        <TextField
                            fullWidth
                            placeholder={t('BankAccountNumber')}
                            value={bankAccountNumber}
                            onChange={(e) => handleChangeCreateBank('bankAccountNumber', e.target.value)}
                            variant="standard"
                            className={fieldStyles.styleInput}
                            InputProps={comboInputProps}
                            inputProps={{ style: { textAlign: fieldTextAlign } }}
                            required
                        />
                    </div>

                    <div>
                        <span className={nestedStyles.fieldLabel}>{t('AccountCard')} *</span>
                        <Autocomplete
                            className={fieldStyles.combo}
                            options={formattedAccountCardOptions}
                            getOptionLabel={(option) =>
                                option.value1 === 'AddItem'
                                    ? (option.name || '')
                                    : `${option.cardId} - ${option.name}`
                            }
                            value={
                                formattedAccountCardOptions.find(
                                    (option) => option.value1 !== 'AddItem' && option.value === cardForNewBank
                                ) || null
                            }
                            isOptionEqualToValue={(option, value) =>
                                option.value1 !== 'AddItem' && String(option.value) === String(value?.value)
                            }
                            onChange={(event, newValue) => {
                                handleCreditDebitForJournalEntry(newValue);
                            }}
                            onInputChange={(event, inputValue, reason) => {
                                if (reason === 'reset') return;
                                setCreditDebitSearchTerm(inputValue);
                            }}
                            PopperComponent={renderComboPopper}
                            noOptionsText={
                                <button
                                    type="button"
                                    className={fieldStyles.addOption}
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        handleNoOptionCreditDebitClick(creditDebitSearchTerm);
                                    }}
                                >
                                    <span className={fieldStyles.addOptionPlus} aria-hidden="true">+</span>
                                    <span className={fieldStyles.addOptionLabel}>
                                        {t('AddAccountCard')}{creditDebitSearchTerm ? ` "${creditDebitSearchTerm}"` : ''}
                                    </span>
                                </button>
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('AccountCard')}
                                    variant="standard"
                                    className={fieldStyles.styleInput}
                                    InputProps={{
                                        ...params.InputProps,
                                        ...comboInputProps,
                                    }}
                                    inputProps={{
                                        ...params.inputProps,
                                        style: { textAlign: fieldTextAlign },
                                    }}
                                />
                            )}
                            renderOption={(props, option) => {
                                const { key, ...optionProps } = props;
                                if (option.value1 === 'AddItem') {
                                    return (
                                        <li
                                            key={key}
                                            {...optionProps}
                                            className={`${optionProps.className || ''} ${fieldStyles.addOption}`}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                openAddAccountCard(creditDebitSearchTerm);
                                            }}
                                        >
                                            <span className={fieldStyles.addOptionPlus} aria-hidden="true">+</span>
                                            <span className={fieldStyles.addOptionLabel}>{option.name}</span>
                                        </li>
                                    );
                                }
                                return (
                                    <Tooltip
                                        key={key}
                                        title={
                                            option.isEditable ? t('RightClickToEdit') : t('NotEditable')
                                        }
                                        placement="top"
                                    >
                                        <li
                                            {...optionProps}
                                            onContextMenu={(event) => {
                                                if (!option.isEditable) {
                                                    event.preventDefault();
                                                    return;
                                                }
                                                event.preventDefault();
                                                EditAccountCard(option, 'cardForNewBank');
                                            }}
                                        >
                                            {`${option?.cardId || ''} - ${option.name}`}
                                        </li>
                                    </Tooltip>
                                );
                            }}
                        />
                    </div>
                </DialogContent>

                <DialogActions className={nestedStyles.footer}>
                    <button
                        type="button"
                        className={nestedStyles.secondaryBtn}
                        onClick={onClose}
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        type="button"
                        className={nestedStyles.primaryBtn}
                        onClick={() => handleAddBank()}
                        disabled={!isFormValid}
                    >
                        {t('SetBank')}
                    </button>
                </DialogActions>
            </div>

        </Dialog>
            <AddAccountCardModal
                accountCardOptions={processedCardsData}
                open={isAddAccountCard}
                onClose={() => setIsAddAccountCard(false)}
                editedItem={newAccountCard}
                isEdit={isEditAccountCard}
                direction={direction}
                client={client}
                token={token}
                setLoading={setLoading}
                handleChangeAfterAdd={handleChangeAfterAdd}
                fetchEnums={fetchEnums}
                type={addAccountCardType}
                palCodes={palCodes}
                propPalCode={palCode}
                setPalCode={setPalCode}
            />
        </>
    );
};

export default AddBankModal;
